import { BreadcrumbProps } from '@blueprintjs/core'
import { computed, makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { Fragment, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ActionBar } from '../components/ActionBar'
import { MatchTypeSmall } from '../components/badge/MatchTypeSmall'
import { Actions } from '../components/details/UpdateActions'
import { Layout } from '../components/Layout'
import { LogsTable } from '../components/logs/LogsTable'
import { TableStore } from '../components/table2/table_store'
import { TargetACOS } from '../components/TargetACOS'
import { TabbedCards } from '../components/ui/TabbedCards'
import { EventParentTypes } from '../config'
import { useOnProfileChange } from '../hooks/useOnProfileChange'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import { useUser } from '../hooks/useUser'
import { useTableHooksFromStore } from '../packages/blueprint-components/src/useTableHooks'
import { userStore } from '../stores/user_store'
import { axiosGet, axiosPatch } from '../util/api'
import { adManagerPerformanceGet } from '../util/adManagerPerformanceApi'
import { OverviewCharts } from './dashboard-overview-charts'

interface SubStores {
    logs: TableStore<any>
}

class DetailStore {
    activeTab: string = ''
    subStores: SubStores

    constructor(subStores: SubStores) {
        this.subStores = subStores
        makeAutoObservable(this)
    }

    setActiveTab(tab: string) {
        this.activeTab = tab
    }

    get dates(): { from: Date; to: Date } {
        switch (this.activeTab) {
            case 'logs':
                return {
                    from: this.subStores.logs.params.from,
                    to: this.subStores.logs.params.to,
                }
            default:
                console.warn('unknown tab', this.activeTab)
                return {
                    // 30 days ago
                    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    to: new Date(),
                }
        }
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() / 86400 === b.getTime() / 86400 })
    get from() {
        return this.dates.from
    }

    @computed({ equals: (a: Date, b: Date) => a.getTime() / 86400 === b.getTime() / 86400 })
    get to() {
        return this.dates.to
    }
}

/**
 * Keyword Detail Page (v5 - Core/PostgreSQL)
 *
 * Uses v5 API endpoints backed by Core/PostgreSQL for keyword data.
 * This is the only keyword detail implementation.
 */
export const KeywordDetailV5 = observer(() => {
    const { keyword_id } = useParams()
    const [keyword, setKeyword] = useState(null)
    const { profile, searchParams } = useUser()
    const [logsStore] = useState(
        new TableStore<any>(userStore, `logs_for_keyword_v5_${keyword_id}`),
    )
    const [detailStore] = useState(new DetailStore({ logs: logsStore }))
    const savedFiltersStore = useSavedFiltersStore()

    // Load keyword metadata from the v5 Core API.
    const load = (params = null) => {
        if (!profile) {
            return
        }

        adManagerPerformanceGet(`api/v5/keywords/${keyword_id}`, params ?? {}, true).then((res) => {
            setKeyword(res.data)
        })
    }

    // Updates still go through Laravel API
    const update = (payload) => axiosPatch(`keywords/${keyword_id}`, payload)

    // Logs still use Laravel API
    const logsLoad = (params) => {
        if (!profile) {
            return Promise.resolve()
        }

        const p = {
            ...params,
            filter: {
                ...params.filter,
                target_type_id: EventParentTypes.KEYWORD,
                target_id: keyword_id, // MER-2802: keep the URL param as a string; parseInt truncates
            },
        }

        return axiosGet('logs', p)
    }
    const logsTableHooks = useTableHooksFromStore(logsLoad, {}, false, logsStore)

    useOnProfileChange(() => {
        load()
    })

    useEffect(() => {
        load()
    }, [searchParams])

    const actions = (
        <Fragment>
            <ActionBar className="flex items-center justify-between pl-2 pr-4">
                <div className="flex items-center">
                    {keyword && (
                        <Fragment>
                            <MatchTypeSmall
                                className="mr-2"
                                matchType={keyword.match_type}
                                state={keyword.state}
                                parent_ad_group_status={keyword.parent_ad_group_status}
                                parent_campaign_status={keyword.parent_campaign_status}
                            />

                            <div className="flex justify-center mr-2">
                                <span className="mr-1">Target ACoS</span>
                                <TargetACOS
                                    target="keywords"
                                    product={keyword}
                                    mutateItems={(cb) => setKeyword((prev) => cb([prev])[0])}
                                />
                            </div>
                        </Fragment>
                    )}
                </div>
                <Actions name="Keyword" reloadParent={load} data={keyword} update={update} />
            </ActionBar>
        </Fragment>
    )

    const breadcrumbs: BreadcrumbProps[] = [
        { text: 'Manage' },
        { href: '/campaigns/' + keyword?.campaign_id, text: 'Campaign: ' + keyword?.campaign_name },
        { href: '/ad-groups/' + keyword?.ad_group_id, text: 'Ad Group: ' + keyword?.ad_group_name },
        { text: 'Keyword:' },
    ]

    return (
        <Layout primary={keyword?.keyword_text} breadcrumbs={breadcrumbs} actions={actions}>
            <OverviewCharts
                params={{
                    type: 'keywords',
                    keyword_id,
                    from: detailStore.from,
                    to: detailStore.to,
                    previous: true,
                }}
            />

            <div className="mb-4" />
            <TabbedCards tabs={['Logs']} onChange={(tab) => detailStore.setActiveTab(tab)}>
                <LogsTable
                    nested={true}
                    store={logsStore}
                    savedFiltersStore={savedFiltersStore}
                    tableHooks={logsTableHooks}
                />
            </TabbedCards>
        </Layout>
    )
})
